<script>
    import { onMount } from "svelte";

    import {
        CustomTooltip,
        SvelteTable,
    } from "../../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getText,
        isObjectEmpty,
    } from "../../../Utils/utilFunctions.svelte";

    export let dashboardData = {},
        filters = {};

    $: columns = [
        {
            key: "surgeon",
            title: "Surgeon",
            headerClass: "text-left pl-3 sortable-header-decoration w-2/3",
            class: "text-left pl-3 cursor-default",
            renderValue: (v) => getText(v.surgeonName, "-"),
        },
        {
            key: "total",
            title: "Total",
            headerClass: "text-left pl-3 sortable-header-decoration w-1/6",
            class: "text-left pl-3 cursor-default",
            renderValue: (v) => getAmountInCurrency(v.totalPayments),
        },
        {
            key: "hourlyRate",
            title: "Hourly Rate",
            headerClass: "text-left pl-3 sortable-header-decoration w-1/6",
            class: "text-left pl-3 cursor-default",
            renderValue: (v) => getAmountInCurrency(v.hourlyRate),
        },
    ];

    let isParsingData = true,
        defaultSurgeons = [],
        sortedSurgeons = [],
        sortOrder = "";

    let averageItem = {};

    onMount(() => {
        if (dashboardData && !isObjectEmpty(dashboardData)) syncSurgeonsData();
        else isParsingData = false;
    });

    const syncSurgeonsData = () => {
        sortOrder = "";

        let { surgeonPayments } = dashboardData;

        surgeonPayments = surgeonPayments.map((payment) => ({
            ...payment,
            hourlyRate: _getHourlyRate(payment),
        }));

        if (!!surgeonPayments.length)
            averageItem = _getAverageItem(surgeonPayments);

        defaultSurgeons = sortedSurgeons = surgeonPayments;

        isParsingData = false;
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (!sortOrder) sortOrder = key;
        else if (sortOrder == key) sortOrder = `${key}Desc`;
        else sortOrder = key;

        handleSortSurgeons();
    };

    const handleSortSurgeons = () => {
        isParsingData = true;

        if (!sortOrder) resetSurgeons();
        else if (sortOrder.includes("surgeon")) {
            if (sortOrder.includes("Desc"))
                sortedSurgeons = defaultSurgeons.sort((a, b) =>
                    b.surgeonName.localeCompare(a.surgeonName),
                );
            else
                sortedSurgeons = defaultSurgeons.sort((a, b) =>
                    a.surgeonName.localeCompare(b.surgeonName),
                );
        } else if (sortOrder.includes("total")) {
            if (sortOrder.includes("Desc"))
                sortedSurgeons = defaultSurgeons.sort(
                    (a, b) => b.totalPayments - a.totalPayments,
                );
            else
                sortedSurgeons = defaultSurgeons.sort(
                    (a, b) => a.totalPayments - b.totalPayments,
                );
        } else if (sortOrder.includes("hourlyRate")) {
            if (sortOrder.includes("Desc"))
                sortedSurgeons = defaultSurgeons.sort(
                    (a, b) => b.hourlyRate - a.hourlyRate,
                );
            else
                sortedSurgeons = defaultSurgeons.sort(
                    (a, b) => a.hourlyRate - b.hourlyRate,
                );
        } else resetSurgeons();

        isParsingData = false;

        setTimeout(() => {
            _updateHeaderSortIcon();
        }, 0);
    };

    const resetSurgeons = () => {
        sortedSurgeons = defaultSurgeons;
        sortOrder = "";
    };

    // HELPER FUNCTIONS
    const _getHourlyRate = (payment) => {
        const { hours, minutes, totalPayments } = payment;

        const totalHours = hours + minutes / 60;

        if (totalHours == 0) return totalPayments;

        return totalPayments / totalHours;
    };

    const _getAverageItem = (payments) => {
        const avePayment =
            payments.reduce((acc, payment) => acc + payment.totalPayments, 0) /
            payments.length;
        const aveHourlyRate =
            payments.reduce(
                (acc, payment) => acc + _getHourlyRate(payment),
                0,
            ) / payments.length;

        return {
            surgeonName: "Average",
            totalPayments: avePayment,
            hourlyRate: aveHourlyRate,
        };
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");

        const upSpan = document.createElement("span");
        const downSpan = document.createElement("span");
        upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
        upSpan.style = "font-size: 10px;";
        downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
        downSpan.style = "font-size: 10px;";

        Array.from(thRow).forEach((row) => {
            const existingSpans = row.getElementsByTagName("span");
            while (existingSpans[0]) {
                existingSpans[0].remove();
            }

            if (
                (sortOrder == "surgeon" &&
                    row.childNodes[0].data == "Surgeon") ||
                (sortOrder == "total" && row.childNodes[0].data == "Total") ||
                (sortOrder == "hourlyRate" &&
                    row.childNodes[0].data == "Hourly Rate")
            )
                row.appendChild(upSpan);
            else if (
                (sortOrder == "surgeonDesc" &&
                    row.childNodes[0].data == "Surgeon") ||
                (sortOrder == "totalDesc" &&
                    row.childNodes[0].data == "Total") ||
                (sortOrder == "hourlyRateDesc" &&
                    row.childNodes[0].data == "Hourly Rate")
            )
                row.appendChild(downSpan);
        });
    };
</script>

<div class="flex flex-col flex-1 gap-4 p-4">
    <div class="flex w-full items-center justify-end z-[2]">
        <CustomTooltip>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                class="stroke-current shrink-0 w-4 h-4"
                slot="tooltipTrigger"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>

            <div>
                <p>
                    This board gives you an estimate of your hourly rate for
                    every surgeon you work with for the defined period. It does
                    not consider "change over" times or any other delays that
                    may have occurred during a list.
                </p>

                <p>Formulas:</p>

                <p>
                    1. Total = Total payments received MINUS refunds from Active
                    Encounters with Status: "Completed".
                </p>

                <p>
                    2. Hourly rate = Total DIVIDED by the anaesthesia time from
                    these encounters.
                </p>

                <p>
                    You can re-order the board by pressing the headers, "Name",
                    "Total" and "Hourly Rate".
                </p>
            </div>
        </CustomTooltip>
    </div>

    {#if isParsingData}
        <div class="flex flex-1 items-center text-center">Parsing data</div>
    {:else if !sortedSurgeons.length}
        <div class="flex flex-1 items-center text-center">
            No surgeons to show
        </div>
    {:else}
        <div class="flex flex-1">
            <SvelteTable
                {columns}
                rows={[...sortedSurgeons, { ...averageItem }]}
                clickColFunc={({ detail: { event, row, key } }) => {
                    handleClickCol(event, row, key);
                }}
            />
        </div>
    {/if}
</div>
